import _ from 'lodash';
import {
  SET_BOOKING_VALUE,
  SET_BOOKING_VALIDITY,
  BOOKING_PRICE_RECEIVE,
  BOOKING_PRICE_FAIL,
  BookingValueAction,
  BookingVadilityAction,
  BookingPriceAction,
  BookingPriceReceiveAction,
  BookingFetchPageRequest,
  BookingFetchPageSuccess,
} from '../actions/types';
import { FETCH_PAGE_REQUEST, FETCH_PAGE_SUCCESS } from '../../../routes/actions/types';
import { BOOKING } from '../../../routes/config/pageTypes';
import { ProcessingState } from '../../../routes/reducers/types';

const initialState: ProcessingState = {
  accommodation_id: -1,
  arrival: '',
  duration_of_stay: 0,
  number_of_adults: 1,
  number_of_children: 0,
  number_of_infants: 0,
  number_of_pets: 0,
  insurance_country: '',
  comment: '',
  payment_method: 'bank_transfer',
  booking_id: -1,
  amount: -1,
  insurance_terms_of_service: false,
  max_persons: 0,
  max_children: 0,
  max_infants: 0,
  current_persons: 0,
  salutation: 'H',
  first_name: '',
  last_name: '',
  birthday: '',
  invoice_street: '',
  invoice_postal_code: '',
  invoice_city: '',
  invoice_phone: '',
  invoice_mobile_phone: '',
  email: '',
  newsletter: true,
  terms_of_service: false,
  isValid: {
    first_name: false,
    last_name: false,
    birthday: false,
    invoice_street: false,
    invoice_postal_code: false,
    invoice_city: false,
    invoice_mobile_phone: false,
    email: false,
    terms_of_service: false,
  },
  is_holiday_home: false,
  disabled: true,
};

function travelerCalculator(
  state: ProcessingState,
): Pick<
  ProcessingState,
  'max_children' | 'max_infants' | 'number_of_adults' | 'number_of_children' | 'number_of_infants'
> {
  const correctedNrAdults = Math.min(state.number_of_adults, state.max_persons);
  const maxChildren = state.max_persons - correctedNrAdults;
  const correctedNrChildren = Math.min(state.number_of_children, maxChildren);
  const maxInfants = state.is_holiday_home
    ? correctedNrChildren
    : maxChildren - correctedNrChildren;
  const correctedNrInfants = Math.min(state.number_of_infants, maxInfants);
  return {
    max_children: maxChildren,
    max_infants: maxInfants,
    number_of_adults: correctedNrAdults,
    number_of_children: correctedNrChildren,
    number_of_infants: correctedNrInfants,
  };
}

const travelerReducer = (
  state: ProcessingState,
  action: { name: string; value: number },
): ProcessingState => {
  switch (action.name) {
    case 'number_of_adults':
    case 'number_of_children':
    case 'number_of_infants':
      return {
        ...state,
        ...travelerCalculator({
          ...state,
          [action.name]: action.value,
        }),
      };
    default:
      return state;
  }
};

export default function reducer(
  state: ProcessingState = initialState,
  action:
    | BookingValueAction
    | BookingVadilityAction
    | BookingPriceAction
    | BookingPriceReceiveAction
    | BookingFetchPageRequest
    | BookingFetchPageSuccess,
): ProcessingState {
  switch (action.type) {
    case FETCH_PAGE_REQUEST: {
      if (action.pageType === BOOKING) {
        const numPersons = _.get(action.location, 'state.body.num_persons');
        if (Number.isNaN(numPersons)) return state;
        return {
          ...state,
          number_of_adults: Number(numPersons),
        };
      }
      return state;
    }
    case FETCH_PAGE_SUCCESS: {
      if (action.pageType === BOOKING) {
        const data = action.data || {};
        return {
          ...state,
          accommodation_id: data.accommodation_id,
          arrival: data.arrival,
          duration_of_stay: data.duration_of_stay,
          insurance_country: data.location_ins,
          comment: data.comment || '',
          payment_method: data.payment_method,
          booking_id: data.booking_id,
          amount: data.amount,
          max_persons: data.max_persons,
          max_children: data.max_persons - state.number_of_adults,
          max_infants: data.max_persons - state.number_of_adults,
          current_persons: data.current_persons,
          is_holiday_home: data.is_holiday_home,
          isValid: {
            ...state.isValid,
            birthday: !data.is_holiday_home,
            terms_of_service: data.is_holiday_home,
          },
        };
      }
      return state;
    }
    case SET_BOOKING_VALUE: {
      if (/number_of_(adults|children|infants)/.test(action.name)) {
        return travelerReducer(state, action);
      }
      return {
        ...state,
        [action.name]: action.value,
      };
    }
    case SET_BOOKING_VALIDITY: {
      const isValid = {
        ...state.isValid,
        [action.name]: action.isValid,
      };
      const disabled = !_.every(_.values(isValid));
      return {
        ...state,
        isValid,
        disabled,
      };
    }
    case BOOKING_PRICE_FAIL:
      return {
        ...state,
        amount: NaN,
      };
    case BOOKING_PRICE_RECEIVE:
      return {
        ...state,
        amount: action.bookingPrice,
      };
    default:
      return state;
  }
}
